import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const HIDE_ON_PATH_PREFIX = ['/admin-login', '/admin-dashboard'];

const STEPS = [
  'Create an account or sign in.',
  'Click on the Basic Questionnaire.',
  'Select structural type & submit details.',
  'Unlock the Advanced Questionnaire.'
];

export default function HowItWorksWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const isLandingRoute = location.pathname === '/' || location.pathname === '/oshm';

  const isHidden = useMemo(
    () => HIDE_ON_PATH_PREFIX.some((prefix) => location.pathname.startsWith(prefix)),
    [location.pathname]
  );

  if (isHidden || !isLandingRoute) {
    return null;
  }

  const topOffset = 'calc(var(--navbar-height, 80px) + 100px)';

  return (
    <div
      className="fixed right-4 z-40"
      style={{ top: topOffset }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="relative flex items-start justify-end">
        {/* Floating Help Button */}
        <button
          type="button"
          aria-label="How it works"
          className="animate-bounce relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-red-600 to-red-800 text-white shadow-xl hover:shadow-red-500/50 hover:scale-105 transition-all duration-300 border-[3px] border-white group"
        >
          <svg className="w-6 h-6 transform group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {/* Popover Content */}
        {isOpen && (
          <div className="absolute right-16 top-0 w-80 bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-2xl p-6 transform transition-all duration-300 opacity-100 scale-100 origin-top-right">
            <h4 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2 border-b border-gray-100 pb-3">
              <span className="bg-red-100 text-red-600 p-1.5 rounded-lg">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </span>
              How it works
            </h4>

            <div className="relative border-l-2 border-red-100 ml-3 space-y-6">
              {STEPS.map((step, index) => (
                <div key={index} className="relative pl-6">
                  {/* Step Marker */}
                  <span className="absolute -left-[11px] top-0.5 flex w-5 h-5 items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold ring-4 ring-white shadow-sm">
                    {index + 1}
                  </span>

                  {/* Step Content */}
                  <h5 className="text-sm font-semibold text-gray-800 leading-tight mb-1">
                    Step {index + 1}
                  </h5>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
